// WebSocket service for real-time reservation updates
import { Client } from '@stomp/stompjs';
import type { IMessage, IFrame } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import type { BookingAction, BookingResponse } from '../types/reservations';
import { WEBSOCKET_CONFIG } from '../constants/config';
import { Subject } from 'rxjs';

export interface Notification {
  id: number;
  bookingId: number;
  type: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export interface PendingBookingsResponse {
  content: BookingResponse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export class WebSocketService {
  private client: Client | null = null;
  private connected = false;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = WEBSOCKET_CONFIG.MAX_RECONNECT_ATTEMPTS;
  private readonly reconnectDelay = WEBSOCKET_CONFIG.RECONNECT_DELAY;
  private fallbackMode = false; // Track if we're in fallback mode
  private currentEndpointIndex = 0; // Track which endpoint we're trying
  private static instance: WebSocketService | null = null;
  private readonly componentSubscribers = new Set<string>(); // Track which components are using the service

  // Subjects for observable pattern (similar to Angular example)
  private readonly reservationUpdates$ = new Subject<BookingResponse>();
  private readonly notifications$ = new Subject<Notification>();
  private readonly pendingBookings$ = new Subject<PendingBookingsResponse>();
  private readonly connectionStatus$ = new Subject<boolean>();

  // Lazy-load endpoints to ensure environment variables are loaded
  private getEndpoints(): string[] {
    // SockJS uses HTTP/HTTPS URLs and upgrades to WebSocket internally
    // WS_URL already contains the full path: /api/v1/ws
   const endpoint = `${WEBSOCKET_CONFIG.WS_URL}${WEBSOCKET_CONFIG.SLUG_WS}`;
    return [endpoint];
  }

  // Private constructor to enforce singleton pattern
  private constructor() {
    this.connect();
  }

  // Static method to get singleton instance
  public static getInstance(): WebSocketService {
    WebSocketService.instance ??= new WebSocketService();
    return WebSocketService.instance;
  }

  // Static method to reset singleton instance for testing
  public static resetInstanceForTesting(): void {
    if (WebSocketService.instance) {
      WebSocketService.instance.disconnect();
      WebSocketService.instance = null;
    }
  }

  // Register a component as using the WebSocket service
  public registerComponent(componentId: string): void {
    this.componentSubscribers.add(componentId);
  }

  // Unregister a component when it unmounts
  public unregisterComponent(componentId: string): void {
    this.componentSubscribers.delete(componentId);

    // Only disconnect if no components are using the service
    if (this.componentSubscribers.size === 0) {
      // Don't disconnect - keep connection alive for better UX
    }
  }

  public connect(): void {
    try {
      // Get access token for authenticated WebSocket connection
      let token: string | null = null;
      if (globalThis.window != null) {
        token = globalThis.window.localStorage.getItem('grandpittu_admin_token');
      }

      const endpoints = this.getEndpoints();
      const currentEndpoint = endpoints[this.currentEndpointIndex];

      if (!currentEndpoint) {
        this.handleReconnect();
        return;
      }


      // Create STOMP client with SockJS (most Spring Boot setups require SockJS)
      this.client = new Client({
        // Use SockJS for compatibility with Spring Boot WebSocket configuration
        webSocketFactory: () => {
          const urlWithAuth = token ? `${currentEndpoint}?token=${encodeURIComponent(token)}` : currentEndpoint;
          return new SockJS(urlWithAuth, null, {
            // Optimize connection speed
            timeout: 3000,          // Reduce timeout from default 5s to 3s
            transports: ['websocket', 'xhr-streaming', 'xhr-polling']  // Skip iframe/jsonp
          });
        },
        // Include token in connect headers as well (backup authentication method)
        connectHeaders: token ? {
          'Authorization': `Bearer ${token}`,
          'X-Auth-Token': token
        } : {},
        debug: (str: string): void => {
          if (WEBSOCKET_CONFIG.DEBUG) {
          }
        },
        reconnectDelay: this.reconnectDelay,
        heartbeatIncoming: WEBSOCKET_CONFIG.HEARTBEAT_INCOMING,
        heartbeatOutgoing: WEBSOCKET_CONFIG.HEARTBEAT_OUTGOING,
      });

      this.client.onConnect = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.currentEndpointIndex = 0; // Reset to first endpoint for next connection
        this.connectionStatus$.next(true);
        this.setupSubscriptions();
      };

      this.client.onDisconnect = () => {
        this.connected = false;
        this.connectionStatus$.next(false);
      };

      this.client.onStompError = (frame: IFrame): void => {
        // Try next endpoint if current one fails
        this.tryNextEndpoint();
      };

      this.client.onWebSocketError = (evt: Event): void => {
        // Try next endpoint if current one fails
        this.tryNextEndpoint();
      };

      this.client.activate();

    } catch (error) {
      this.handleReconnect();
    }
  }

  private setupSubscriptions(): void {
    if (!this.client || !this.connected) return;

    try {
      // Subscribe to admin notifications (from your Spring Boot service)
      this.client.subscribe('/topic/adminNotifications', (msg: IMessage) => {
        try {
          const notification: Notification = JSON.parse(msg.body);
          this.notifications$.next(notification);
        } catch (error) {
        }
      });

      // Subscribe to pending bookings updates (from your Spring Boot service)
      this.client.subscribe('/topic/getAllPendingBookingDetails', (msg: IMessage) => {
        try {
          const pendingBookings: PendingBookingsResponse = JSON.parse(msg.body);
          this.pendingBookings$.next(pendingBookings);
        } catch (error) {
        }
      });

      // Subscribe to booking updates
      this.client.subscribe('/topic/bookingUpdates', (msg: IMessage) => {
        try {
          const booking: BookingResponse = JSON.parse(msg.body);
          this.reservationUpdates$.next(booking);
        } catch (error) {
        }
      });

      // Keep your existing subscriptions if needed
      // this.client.subscribe('/topic/tableUpdates', (msg: IMessage) => {
      //   try {
      //     console.log('📌 Table Update:', JSON.parse(msg.body));
      //   } catch (error) {
      //     console.error('❌ Error parsing table update:', error);
      //   }
      // });


    } catch (error) {
    }
  }

  private tryNextEndpoint(): void {
    const endpoints = this.getEndpoints();
    if (this.currentEndpointIndex < endpoints.length - 1) {
      this.currentEndpointIndex++;

      // Disconnect current client and try with next endpoint
      if (this.client) {
        this.client.deactivate();
      }

      // Small delay before trying next endpoint
      setTimeout(() => {
        this.connect();
      }, 1000);
    } else {
      this.currentEndpointIndex = 0; // Reset for next full retry cycle
      this.handleReconnect();
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.fallbackMode = true;
      this.connected = false;
      this.connectionStatus$.next(false);

      // Dispatch a custom event to notify the app that WebSocket is in fallback mode
      if (globalThis.window != null) {
        globalThis.window.dispatchEvent(new CustomEvent('websocket-fallback', {
          detail: { message: 'WebSocket connection failed, using fallback mode' }
        }));
      }

      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff


    setTimeout(() => {
      if (!this.connected && !this.fallbackMode) {
        this.connect();
      }
    }, delay);
  }

  // Send booking action (similar to Angular example)
  public sendBookingAction(bookingAction: BookingAction): void {
    if (this.connected) {
      this.client?.publish({
        destination: '/app/bookingAction',
        body: JSON.stringify(bookingAction),
      });
    } else {
    }
  }

  // Send new booking (similar to Angular example)
  public sendBooking(booking: BookingResponse): void {
    if (this.connected) {
      this.client?.publish({
        destination: '/app/createBooking',
        body: JSON.stringify(booking),
      });
    } else {
      console.error('WebSocket not connected yet!');
    }
  }

  // Observable getters (similar to Angular example)
  public bookingUpdates() {
    return this.reservationUpdates$.asObservable();
  }

  public notifications() {
    return this.notifications$.asObservable();
  }

  public pendingBookings() {
    return this.pendingBookings$.asObservable();
  }

  public connectionStatus() {
    return this.connectionStatus$.asObservable();
  }

  // Public API
  public isConnected(): boolean {
    return this.connected;
  }

  public isInFallbackMode(): boolean {
    return this.fallbackMode;
  }

  public getConnectionStatus(): string {
    if (this.connected) return 'connected';
    if (this.fallbackMode) return 'fallback';
    return 'disconnected';
  }

  public disconnect(): void {
    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }
    this.connected = false;
    this.fallbackMode = false;
    this.connectionStatus$.next(false);
  }

  public reconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.fallbackMode = false;
    setTimeout(() => this.connect(), 100);
  }

  // Trigger a manual refresh event when in fallback mode
  public triggerManualRefresh(): void {
    if (this.fallbackMode) {
      if (globalThis.window != null) {
        globalThis.window.dispatchEvent(new CustomEvent('manual-refresh-needed'));
      }
    }
  }
}

// Export singleton instance
export const webSocketService = WebSocketService.getInstance();
export default webSocketService;